import { useRef } from 'react'
import { useAppSelector } from '@services/store'
import {
	REQUEST_CONFIGS_KEYS,
	REQUEST_TN_DETAILS_STEPS_NAME
} from '@services/constants'
import requestForm from '../../request.form'
import * as utilsList from '../__index.utils'
import { isGR7_1RenovationOrDevelopment } from '../__index.utils'

export const STEP1FIELDS = {
	whatToDo: 'whatToDo',
	requestAs: 'requestAs',
	companyName: 'companyName',
	representativeName: 'representativeName',
	isSubjectToPiiaApproval: 'isSubjectToPiiaApproval',
	hasBeenSubjectToApproval: 'hasBeenSubjectToApproval',
	iaNumber: 'iaNumber',
	ecResolutionNumber: 'ecResolutionNumber'
} as const

export type Step1Fields = keyof typeof STEP1FIELDS

/**
 * useStep1State - Custom hook to manage the state for step 1 of the GR7 commercial Permit form.
 * It retrieves the necessary answers from the Redux store and prepares the state for the form inputs.
 *
 * @returns {Object} An object containing the state for each input in step 1.
 */

export function useStep1State() {
	const answers = useAppSelector((state) => state.request.configs)
	const { pageAssets } = requestForm()

	const radioListYesNo = utilsList.radioListYesNo(pageAssets)

	const isGR7_1 = isGR7_1RenovationOrDevelopment(answers?.whatToDo)

	return {
		// Que souhaitez-vous faire?
		whatToDo: {
			name: 'whatToDo',
			label: pageAssets?.request_form_what_would_you_like_to_do,
			labelKey: 'request_form_what_would_you_like_to_do',
			value: answers?.whatToDo?.toString() || '',
			required: true,
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// Q2 Vous faites cette demande en tant que:
		requestAs: {
			name: 'requestAs',
			label: pageAssets?.request_form_request_as,
			labelKey: 'request_form_request_as',
			value: answers?.requestAs?.toString() || '',
			required: true,
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// ************* Informations relatives à l'entreprise ***********
		// Nom de l'entreprise
		companyName: {
			name: 'companyName',
			label:
				pageAssets?.request_form_annualRegistrationCertificateForContractors_companyName_label,
			labelKey:
				'request_form_annualRegistrationCertificateForContractors_companyName_label',
			value: answers.companyName || '',
			required:
				answers.requestAs?.toString() ===
				REQUEST_CONFIGS_KEYS.requestAs.entreprise,
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// Nom et prénom du représentant
		representativeName: {
			name: 'representativeName',
			label:
				pageAssets?.request_form_swimmingPoolSpaPermit_representativeName_label,
			labelKey: 'request_form_swimmingPoolSpaPermit_representativeName_label',
			value: answers.representativeName || '',
			required: [
				REQUEST_CONFIGS_KEYS.requestAs.entreprise,
				REQUEST_CONFIGS_KEYS.requestAs.entrepreneur_or_manager
			].includes(answers.requestAs?.toString() || ''),
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// **** Plan d'implantation et d'intégration architecturale (PIIA) *****
		// Est-ce que les travaux sont assujettis à une approbation PIIA?
		isSubjectToPiiaApproval: {
			name: 'isSubjectToPiiaApproval',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_isSubjectToPiiaApproval_label,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_isSubjectToPiiaApproval_label',
			description:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_isSubjectToPiiaApproval_description,
			value: answers.isSubjectToPiiaApproval?.toString() || '',
			required: isGR7_1,
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// Le projet a-t-il reçu une approbation PIIA?
		hasBeenSubjectToApproval: {
			name: 'hasBeenSubjectToApproval',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_hasBeenSubjectToApproval_label,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_hasBeenSubjectToApproval_label',
			value: answers.hasBeenSubjectToApproval?.toString() || '',
			required:
				isGR7_1 && answers.isSubjectToPiiaApproval?.toString() === 'true',
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// 	IA number (architectural integration)
		iaNumber: {
			name: 'iaNumber',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_iaNumber_label,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_iaNumber_label',
			value: answers.iaNumber || '',
			required:
				isGR7_1 &&
				answers.hasBeenSubjectToApproval?.toString() === 'true' &&
				answers.isSubjectToPiiaApproval?.toString() === 'true',
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		},
		// Executive Committee (EC) resolution number
		ecResolutionNumber: {
			name: 'ecResolutionNumber',
			label:
				pageAssets?.request_form_buildingConstructionOrAdditionPermit_ecResolutionNumber_label,
			labelKey:
				'request_form_buildingConstructionOrAdditionPermit_ecResolutionNumber_label',
			value: answers.ecResolutionNumber || '',
			required: false,
			ref: useRef(null),
			subStep: REQUEST_TN_DETAILS_STEPS_NAME.DETAILS_1
		}
	}
}
